import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { useCart } from '../../contexts/CartContext'
import { useNavigate } from 'react-router-dom'
import Modal from '../ui/Modal'
import Button from '../ui/Button'
import { CartItemCard } from '../cart/CartItemCard'
import { CartSummary } from '../cart/CartSummary'
import { supabase } from '../../lib/supabase'
import toast from 'react-hot-toast'
import { DocumentTextIcon, CalendarIcon } from '@heroicons/react/24/outline'

const invoiceBuilderSchema = z.object({
  due_date: z.string().min(1, 'Due date is required'),
  payment_terms: z.string().default('Net 30'),
  notes: z.string().optional(),
  tax_rate: z.number().min(0).max(100).default(0),
  discount_amount: z.number().min(0).default(0),
  discount_type: z.enum(['fixed', 'percentage']).default('fixed'),
})

type InvoiceBuilderFormData = z.infer<typeof invoiceBuilderSchema>

interface InvoiceBuilderProps {
  isOpen: boolean
  onClose: () => void
  clientId?: string
}

export function InvoiceBuilder({ isOpen, onClose, clientId }: InvoiceBuilderProps) {
  const navigate = useNavigate()
  const { cart, items, totalAmount, clearCart } = useCart()
  const [selectedItems, setSelectedItems] = useState<string[]>([])

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<InvoiceBuilderFormData>({
    resolver: zodResolver(invoiceBuilderSchema),
    defaultValues: {
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      payment_terms: 'Net 30',
      tax_rate: 0,
      discount_amount: 0,
      discount_type: 'fixed',
    },
  })

  const taxRate = watch('tax_rate')
  const discountAmount = watch('discount_amount')
  const discountType = watch('discount_type')

  // Select all items by default
  React.useEffect(() => {
    if (isOpen && items.length > 0 && selectedItems.length === 0) {
      setSelectedItems(items.map(item => item.id))
    }
  }, [isOpen, items])

  const createInvoiceMutation = useMutation({
    mutationFn: async (data: InvoiceBuilderFormData) => {
      if (!cart) throw new Error('No cart found')
      
      // Calculate amounts
      const selectedCartItems = items.filter(item => selectedItems.includes(item.id))
      const subtotal = selectedCartItems.reduce((sum, item) => sum + Number(item.total_price), 0)
      const taxAmount = subtotal * (data.tax_rate / 100)
      const discountValue = discountType === 'percentage' 
        ? subtotal * (data.discount_amount / 100)
        : data.discount_amount
      const total = subtotal + taxAmount - discountValue

      // Convert cart to invoice using our function
      const { data: invoiceData, error } = await supabase.rpc('convert_cart_to_invoice', {
        p_cart_id: cart.id,
        p_due_date: data.due_date,
        p_notes: data.notes,
        p_payment_terms: data.payment_terms
      })

      if (error) throw error

      // Update invoice with tax and discount
      const { error: updateError } = await supabase
        .from('invoices')
        .update({
          tax_amount: taxAmount.toString(),
          discount_amount: discountValue.toString(),
          total_amount: total.toString()
        })
        .eq('id', invoiceData)

      if (updateError) throw updateError

      return invoiceData
    },
    onSuccess: (invoiceId) => {
      toast.success('Invoice created successfully!')
      clearCart()
      onClose()
      navigate(`/billing/invoices/${invoiceId}`)
    },
    onError: (error: Error) => {
      toast.error(`Failed to create invoice: ${error.message}`)
    },
  })

  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([])
    } else {
      setSelectedItems(items.map(item => item.id))
    }
  }

  const calculateSelectedSubtotal = () => {
    return items
      .filter(item => selectedItems.includes(item.id))
      .reduce((sum, item) => sum + Number(item.total_price), 0)
  }

  const calculateTax = () => {
    return calculateSelectedSubtotal() * (taxRate / 100)
  }

  const calculateDiscount = () => {
    const subtotal = calculateSelectedSubtotal()
    return discountType === 'percentage' 
      ? subtotal * (discountAmount / 100)
      : discountAmount
  }

  const calculateTotal = () => {
    return calculateSelectedSubtotal() + calculateTax() - calculateDiscount()
  }

  const onSubmit = (data: InvoiceBuilderFormData) => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item')
      return
    }
    createInvoiceMutation.mutate(data)
  }

  if (!cart || items.length === 0) {
    return (
      <Modal open={isOpen} onClose={onClose} size="md">
        <Modal.Body>
          <div className="text-center py-8">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-400">No items in cart to create invoice</p>
          </div>
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="xl"
      title="Create Invoice from Cart"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="space-y-6">
            {/* Cart Items Selection */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-gold font-medium">Select Items</h3>
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-sm text-gold hover:text-gold-light"
                >
                  {selectedItems.length === items.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={`cursor-pointer transition-all ${
                      selectedItems.includes(item.id) ? 'ring-2 ring-gold' : ''
                    }`}
                    onClick={() => toggleItemSelection(item.id)}
                  >
                    <CartItemCard item={item} showControls={false} />
                  </div>
                ))}
              </div>
            </div>

            {/* Invoice Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gold mb-1">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Due Date
                </label>
                <input
                  type="date"
                  {...register('due_date')}
                  className="w-full px-3 py-2 bg-black/50 border border-gold/30 rounded-lg text-white"
                />
                {errors.due_date && (
                  <p className="text-red-400 text-xs mt-1">{errors.due_date.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gold mb-1">
                  Payment Terms
                </label>
                <select
                  {...register('payment_terms')}
                  className="w-full px-3 py-2 bg-black/50 border border-gold/30 rounded-lg text-white"
                >
                  <option value="Due on Receipt">Due on Receipt</option>
                  <option value="Net 15">Net 15</option>
                  <option value="Net 30">Net 30</option>
                  <option value="Net 45">Net 45</option>
                  <option value="Net 60">Net 60</option>
                </select>
              </div>
            </div>

            {/* Tax and Discount */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gold mb-1">
                  Tax Rate (%)
                </label>
                <input
                  type="number"
                  {...register('tax_rate', { valueAsNumber: true })}
                  className="w-full px-3 py-2 bg-black/50 border border-gold/30 rounded-lg text-white"
                  step="0.01"
                  min="0"
                  max="100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gold mb-1">
                  Discount
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    {...register('discount_amount', { valueAsNumber: true })}
                    className="flex-1 px-3 py-2 bg-black/50 border border-gold/30 rounded-lg text-white"
                    step="0.01"
                    min="0"
                  />
                  <select
                    {...register('discount_type')}
                    className="px-3 py-2 bg-black/50 border border-gold/30 rounded-lg text-white"
                  >
                    <option value="fixed">$</option>
                    <option value="percentage">%</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gold mb-1">
                Notes
              </label>
              <textarea
                {...register('notes')}
                rows={3}
                className="w-full px-3 py-2 bg-black/50 border border-gold/30 rounded-lg text-white"
                placeholder="Additional notes for the invoice..."
              />
            </div>

            {/* Invoice Preview */}
            <div className="bg-black/30 rounded-lg p-4 space-y-2">
              <h4 className="text-gold font-medium mb-3">Invoice Preview</h4>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Subtotal ({selectedItems.length} items)</span>
                <span className="text-white">${calculateSelectedSubtotal().toFixed(2)}</span>
              </div>

              {taxRate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Tax ({taxRate}%)</span>
                  <span className="text-white">${calculateTax().toFixed(2)}</span>
                </div>
              )}

              {discountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">
                    Discount ({discountAmount}{discountType === 'percentage' ? '%' : '$'})
                  </span>
                  <span className="text-white">-${calculateDiscount().toFixed(2)}</span>
                </div>
              )}

              <div className="flex justify-between text-lg font-medium pt-2 border-t border-gray-700">
                <span className="text-gold">Total Due</span>
                <span className="text-gold">${calculateTotal().toFixed(2)}</span>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || selectedItems.length === 0}
            >
              {isSubmitting ? 'Creating Invoice...' : 'Create Invoice'}
            </Button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  )
}